import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from 'react-i18next';
import { FaHeart, FaRetweet, FaEye, FaExternalLinkAlt } from 'react-icons/fa';

let THEME = {};
const getStyles = () => StyleSheet.create({
    container: { width: '100%' },
    h1: {
        fontSize: 22,
        fontWeight: '700',
        color: THEME.textPrimary,
        marginTop: 4,
        marginBottom: 16,
        lineHeight: 30,
    },
    h2: {
        fontSize: 18,
        fontWeight: '700',
        color: THEME.textPrimary,
        marginTop: 28,
        marginBottom: 12,
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: THEME.border,
    },
    h3: {
        fontSize: 16,
        fontWeight: '600',
        color: THEME.textPrimary,
        marginTop: 18,
        marginBottom: 8,
    },
    paragraph: {
        fontSize: 14,
        color: THEME.textSecondary,
        lineHeight: 22,
        marginBottom: 6,
    },
    listItem: {
        flexDirection: 'row',
        marginTop: 2,
        marginBottom: 6,
        paddingLeft: 4,
    },
    bullet: {
        width: 18,
        color: THEME.accent,
        fontSize: 14,
        lineHeight: 22,
    },
    listText: {
        flex: 1,
        fontSize: 14,
        color: THEME.textSecondary,
        lineHeight: 22,
    },
    spacer: { height: 6 },
    // Social post card
    postCard: {
        backgroundColor: THEME.bgCard,
        borderRadius: 14,
        padding: 16,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: THEME.border,
    },
    postHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    postHandle: {
        fontSize: 14,
        fontWeight: '700',
        color: THEME.textPrimary,
    },
    postRole: {
        fontSize: 12,
        color: THEME.textMuted,
        marginTop: 2,
    },
    postContent: {
        fontSize: 14,
        color: THEME.textSecondary,
        lineHeight: 21,
        marginBottom: 12,
        fontStyle: 'italic',
    },
    postFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: THEME.border,
        flexWrap: 'wrap',
        gap: 8,
    },
    postStats: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
    },
    postStat: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
    },
    postStatNum: {
        fontSize: 13,
        fontWeight: '600',
        color: THEME.textSecondary,
    },
    noteChip: {
        backgroundColor: THEME.bgSurface,
        borderRadius: 8,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderWidth: 1,
        borderColor: THEME.border,
        maxWidth: '55%',
    },
    noteChipText: {
        fontSize: 11,
        color: THEME.textMuted,
        fontWeight: '500',
    },
    // Citation footer section
    citationsSection: {
        marginTop: 20,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: THEME.border,
    },
    citationsLabel: {
        fontSize: 11,
        fontWeight: '700',
        color: THEME.textMuted,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        marginBottom: 10,
    },
    citationsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    citationPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        backgroundColor: THEME.bgSurface,
        borderRadius: 8,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderWidth: 1,
        borderColor: THEME.border,
    },
    citationNum: {
        fontSize: 11,
        fontWeight: '700',
        color: THEME.accent,
    },
    citationUrl: {
        fontSize: 11,
        color: THEME.textMuted,
    },
});
let styles = getStyles();

const isValidUrl = (s) => { try { new URL(s); return true; } catch { return false; } };

const toFullUrl = (url) => {
    if (!url) return null;
    if (isValidUrl(url)) return url;
    if (isValidUrl('https://' + url)) return 'https://' + url;
    return null;
};

// Parse "964 likes, 146 reposts, 124K views"
const parseEngagement = (str) => {
    if (!str) return {};
    const result = {};
    const likesM = str.match(/([\d.,KkMmBb]+)\s*likes?/i);
    const repostsM = str.match(/([\d.,KkMmBb]+)\s*(?:reposts?|retweets?|shares?)/i);
    const viewsM = str.match(/([\d.,KkMmBb]+)\s*views?/i);
    if (likesM) result.likes = likesM[1];
    if (repostsM) result.reposts = repostsM[1];
    if (viewsM) result.views = viewsM[1];
    return result;
};

// Strip bare citation refs [1] from a string
const stripCitations = (s) => s.replace(/\[\d+\]/g, '').trim();

// Parse "@handle (role)" from an account string
const parseHandle = (accountStr) => {
    const cleaned = stripCitations(accountStr);
    const m = cleaned.match(/^(@[\w.]+)/);
    const handle = m ? m[1] : cleaned.split(/[\s(]/)[0];
    const rest = cleaned.replace(/^@[\w.]+\s*/, '').replace(/^\(/, '').replace(/\)$/, '').trim();
    return { handle, role: rest || null };
};

// Try to find citation footer line: "[1] x.com [2] x.com ..."
const parseCitationLine = (line) => {
    const matches = [...line.matchAll(/\[(\d+)\]\s+(https?:\/\/\S+|[\w.-]+\.\w{2,}[^\s\[\]]*)/g)];
    if (matches.length < 2) return null;
    return matches.map(m => ({ num: m[1], url: m[2] }));
};

// Check if a line is a bare number / engagement stat like "964", "2.5K", "124K"
const isStatLine = (s) => /^[\d.,]+[KkMmBb]?$/.test(s.trim());

// Pre-process content into typed blocks
const parseBlocks = (content) => {
    const lines = content.split('\n');
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();

        // Post block detection: line starting with @handle
        if (/^@\w/.test(trimmed)) {
            const post = { handle: trimmed, role: null, content: null, url: null, stats: [], note: null };
            i++;
            // Optional role line (not a quote, not a stat, not another @)
            if (i < lines.length) {
                const next = lines[i].trim();
                if (next && !next.startsWith('"') && !next.startsWith('@') && !isStatLine(next) && !/^[-*#\[]/.test(next)) {
                    post.role = stripCitations(next);
                    i++;
                }
            }
            // Optional quote line
            if (i < lines.length) {
                const next = lines[i].trim();
                if (next.startsWith('"') || next.startsWith('\u201c')) {
                    // extract URL from inline markdown link [](url) or [text](url)
                    const urlM = next.match(/\[.*?\]\((https?:\/\/[^\)]+)\)/);
                    if (urlM) post.url = urlM[1];
                    post.content = next.replace(/\[.*?\]\(https?:\/\/[^\)]+\)/g, '').replace(/[""]/g, '"').replace(/^"|"$/g, '').trim();
                    i++;
                }
            }
            // Stat lines (bare numbers)
            while (i < lines.length && isStatLine(lines[i].trim()) && post.stats.length < 4) {
                const s = lines[i].trim();
                if (s) post.stats.push(s);
                i++;
            }
            // Optional note line
            if (i < lines.length) {
                const next = lines[i].trim();
                if (next && !next.startsWith('@') && !next.startsWith('#') && !next.startsWith('-') && !isStatLine(next)) {
                    post.note = next;
                    i++;
                }
            }
            blocks.push({ type: 'post', post });
            continue;
        }

        // Pipe table detection
        if (trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 2) {
            const tableLines = [];
            while (i < lines.length && lines[i].trim().startsWith('|')) {
                tableLines.push(lines[i]);
                i++;
            }
            if (tableLines.length >= 3) {
                const headers = tableLines[0].split('|')
                    .filter((_, idx, arr) => idx !== 0 && idx !== arr.length - 1)
                    .map(h => h.trim());
                const rows = [];
                for (let j = 2; j < tableLines.length; j++) {
                    const cells = tableLines[j].split('|')
                        .filter((_, idx, arr) => idx !== 0 && idx !== arr.length - 1)
                        .map(c => c.trim());
                    if (cells.some(c => c)) rows.push(cells);
                }
                if (rows.length > 0) {
                    blocks.push({ type: 'table', headers, rows });
                }
            }
            continue;
        }

        // Citation footer detection
        const citPairs = parseCitationLine(trimmed);
        if (citPairs && citPairs.length >= 2) {
            blocks.push({ type: 'citations', pairs: citPairs });
            i++;
            continue;
        }

        blocks.push({ type: 'line', text: line });
        i++;
    }
    return blocks;
};

// Parse inline markdown: **bold**, [text](url), [n] citation refs
const parseInline = (text, _keyPrefix, citationMap) => {
    const parts = [];
    let lastIndex = 0;
    const regex = /(\*\*.*?\*\*)|(\[{1,2}([^\]]*)\]{1,2}\((https?:\/\/[^\)]+)\))|(\[\d+\])/g;
    let m;
    while ((m = regex.exec(text)) !== null) {
        if (m.index > lastIndex) {
            parts.push({ type: 'text', content: text.substring(lastIndex, m.index) });
        }
        const full = m[0];
        if (full.startsWith('**')) {
            parts.push({ type: 'bold', content: full.slice(2, -2) });
        } else if (full.startsWith('[') && full.includes('](')) {
            const linkText = m[3];
            const linkUrl = m[4];
            if (/^\d+$/.test(linkText.trim())) {
                parts.push({ type: 'cite', num: linkText.trim(), url: linkUrl });
            } else {
                parts.push({ type: 'link', content: linkText, url: linkUrl });
            }
        } else if (/^\[\d+\]$/.test(full)) {
            const num = full.slice(1, -1);
            parts.push({ type: 'cite', num, url: citationMap?.[num] });
        }
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
        parts.push({ type: 'text', content: text.substring(lastIndex) });
    }

    return parts.map((part, i) => {
        if (part.type === 'text') return <Text key={i}>{part.content}</Text>;
        if (part.type === 'bold') return <Text key={i} style={{ fontWeight: '700', color: THEME.textPrimary }}>{part.content}</Text>;
        if (part.type === 'cite') {
            const href = toFullUrl(part.url);
            return (
                <Text key={i}> <Text
                    onPress={() => href && window.open(href, '_blank')}
                    style={{
                        color: THEME.textSecondary,
                        fontSize: 10,
                        fontWeight: '600',
                        backgroundColor: THEME.bgSurface,
                        borderRadius: 10,
                        paddingHorizontal: 5,
                        paddingVertical: 2,
                        overflow: 'hidden',
                        verticalAlign: 'middle',
                    }}
                >{part.num}</Text> </Text>
            );
        }
        if (part.type === 'link') {
            return (
                <Text
                    key={i}
                    onPress={() => part.url && window.open(part.url, '_blank')}
                    style={{ color: THEME.accent, textDecorationLine: 'underline' }}
                >
                    {part.content}
                </Text>
            );
        }
        return null;
    });
};

// Render a "Key Voices / Viral Posts" table as social post cards
const renderPostsTable = (headers, rows, idx, citationMap) => {
    const acctIdx = headers.findIndex(h => /account|handle|user|author/i.test(h));
    const contentIdx = headers.findIndex(h => /post|summary|content|message|tweet|text/i.test(h));
    const engIdx = headers.findIndex(h => /engagement|stats|metric|likes|views|reposts/i.test(h));
    const notesIdx = headers.findIndex(h => /note|context|risk|comment/i.test(h));

    const ai = acctIdx >= 0 ? acctIdx : 0;
    const ci = contentIdx >= 0 ? contentIdx : 1;
    const ei = engIdx >= 0 ? engIdx : 2;
    const ni = notesIdx >= 0 ? notesIdx : 3;

    return (
        <View key={`table-${idx}`} style={{ marginTop: 4, marginBottom: 12 }}>
            {rows.map((row, rowIdx) => {
                const { handle, role } = parseHandle(row[ai] || '');
                const rawContent = row[ci] || '';
                const contentText = stripCitations(rawContent);
                const eng = parseEngagement(row[ei] || '');
                const notesText = stripCitations(row[ni] || '');

                // Try to find a source URL from citation in content column
                const citM = rawContent.match(/\[(\d+)\]/);
                const sourceUrl = citM ? toFullUrl(citationMap?.[citM[1]]) : null;

                return (
                    <View key={rowIdx} style={styles.postCard}>
                        {/* Handle + external link */}
                        <View style={styles.postHeader}>
                            <View style={{ flex: 1 }}>
                                <Text style={styles.postHandle}>{handle}</Text>
                                {role ? <Text style={styles.postRole}>{role}</Text> : null}
                            </View>
                            {sourceUrl ? (
                                <TouchableOpacity onPress={() => window.open(sourceUrl, '_blank')} style={{ padding: 6 }}>
                                    <FaExternalLinkAlt size={12} color={THEME.textMuted} />
                                </TouchableOpacity>
                            ) : null}
                        </View>

                        {/* Post content */}
                        {contentText ? (
                            <Text style={styles.postContent}>"{contentText}"</Text>
                        ) : null}

                        {/* Footer: engagement stats + note chip */}
                        <View style={styles.postFooter}>
                            {(eng.likes || eng.reposts || eng.views) ? (
                                <View style={styles.postStats}>
                                    {eng.likes ? (
                                        <View style={styles.postStat}>
                                            <FaHeart size={12} color="#f43f5e" />
                                            <Text style={styles.postStatNum}>{eng.likes}</Text>
                                        </View>
                                    ) : null}
                                    {eng.reposts ? (
                                        <View style={styles.postStat}>
                                            <FaRetweet size={12} color="#22c55e" />
                                            <Text style={styles.postStatNum}>{eng.reposts}</Text>
                                        </View>
                                    ) : null}
                                    {eng.views ? (
                                        <View style={styles.postStat}>
                                            <FaEye size={13} color={THEME.textMuted} />
                                            <Text style={styles.postStatNum}>{eng.views}</Text>
                                        </View>
                                    ) : null}
                                </View>
                            ) : <View />}

                            {notesText ? (
                                <View style={styles.noteChip}>
                                    <Text style={styles.noteChipText} numberOfLines={2}>{notesText}</Text>
                                </View>
                            ) : null}
                        </View>
                    </View>
                );
            })}
        </View>
    );
};

const MarkdownText = ({ content, style }) => {
    const { theme } = useTheme();
    const { t } = useTranslation();
    Object.assign(THEME, theme);
    styles = getStyles();

    if (!content) return null;

    // Build citation map from footer line(s) first
    const citationMap = {};
    content.split('\n').forEach(line => {
        const pairs = parseCitationLine(line.trim());
        if (pairs && pairs.length >= 2) {
            pairs.forEach(({ num, url }) => { citationMap[num] = url; });
        }
    });

    const blocks = parseBlocks(content);

    const renderBlock = (block, idx) => {
        // Inline post block (@handle format)
        if (block.type === 'post') {
            const { post } = block;
            const [likes, reposts, views] = post.stats;
            return (
                <View key={`post-${idx}`} style={styles.postCard}>
                    <View style={styles.postHeader}>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.postHandle}>{post.handle}</Text>
                            {post.role ? <Text style={styles.postRole}>{post.role}</Text> : null}
                        </View>
                        {post.url ? (
                            <TouchableOpacity onPress={() => window.open(post.url, '_blank')} style={{ padding: 6 }}>
                                <FaExternalLinkAlt size={12} color={THEME.textMuted} />
                            </TouchableOpacity>
                        ) : null}
                    </View>
                    {post.content ? (
                        <Text style={styles.postContent}>"{post.content}"</Text>
                    ) : null}
                    <View style={styles.postFooter}>
                        {(likes || reposts || views) ? (
                            <View style={styles.postStats}>
                                {likes ? <View style={styles.postStat}><FaHeart size={12} color="#f43f5e" /><Text style={styles.postStatNum}>{likes}</Text></View> : null}
                                {reposts ? <View style={styles.postStat}><FaRetweet size={12} color="#22c55e" /><Text style={styles.postStatNum}>{reposts}</Text></View> : null}
                                {views ? <View style={styles.postStat}><FaEye size={13} color={THEME.textMuted} /><Text style={styles.postStatNum}>{views}</Text></View> : null}
                            </View>
                        ) : <View />}
                        {post.note ? (
                            <View style={styles.noteChip}>
                                <Text style={styles.noteChipText} numberOfLines={2}>{post.note}</Text>
                            </View>
                        ) : null}
                    </View>
                </View>
            );
        }

        // Citation footer
        if (block.type === 'citations') {
            return (
                <View key={`cit-${idx}`} style={styles.citationsSection}>
                    <Text style={styles.citationsLabel}>{t('app.common.sources')}</Text>
                    <View style={styles.citationsRow}>
                        {block.pairs.map(({ num, url }) => {
                            const href = toFullUrl(url);
                            return (
                                <TouchableOpacity
                                    key={num}
                                    style={styles.citationPill}
                                    onPress={() => href && window.open(href, '_blank')}
                                >
                                    <Text style={styles.citationNum}>[{num}]</Text>
                                    <Text style={styles.citationUrl}>
                                        {url.replace(/^https?:\/\//, '').split('/')[0]}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                </View>
            );
        }

        // Table
        if (block.type === 'table') {
            const isPostTable = block.headers.some(h =>
                /account|handle|user|author|post|tweet|content|engagement|stats|metric|likes|views|reposts/i.test(h)
            );
            if (isPostTable) {
                return renderPostsTable(block.headers, block.rows, idx, citationMap);
            }
            // Generic table: render as indented items
            return (
                <View key={`tbl-${idx}`} style={{ marginBottom: 12 }}>
                    {block.rows.map((row, ri) => (
                        <View key={ri} style={styles.listItem}>
                            <Text style={styles.bullet}>•</Text>
                            <Text style={[styles.listText]}>{row.filter(Boolean).join(' — ')}</Text>
                        </View>
                    ))}
                </View>
            );
        }

        // Line-based rendering
        const { text } = block;
        const trimmed = text.trim();

        // Headers (# ## ###)
        const headerM = trimmed.match(/^(#{1,6})\s+(.+)$/);
        if (headerM) {
            const level = headerM[1].length;
            const headerText = headerM[2].replace(/\*\*/g, '');
            const s = level === 1 ? styles.h1 : level === 2 ? styles.h2 : styles.h3;
            return <Text key={idx} style={s}>{headerText}</Text>;
        }

        // Unordered list (- or *)
        const listM = trimmed.match(/^[-*]\s+(.+)$/);
        if (listM) {
            return (
                <View key={idx} style={styles.listItem}>
                    <Text style={styles.bullet}>•</Text>
                    <Text style={styles.listText}>{parseInline(listM[1], `li-${idx}`, citationMap)}</Text>
                </View>
            );
        }

        // Numbered list (1. 2.)
        const numListM = trimmed.match(/^(\d+)\.\s+(.+)$/);
        if (numListM) {
            return (
                <View key={idx} style={[styles.listItem, { gap: 6 }]}>
                    <Text style={[styles.bullet, { color: THEME.accent, fontWeight: '700', width: 22 }]}>{numListM[1]}.</Text>
                    <Text style={styles.listText}>{parseInline(numListM[2], `nl-${idx}`, citationMap)}</Text>
                </View>
            );
        }

        // Horizontal rule
        if (/^[-*_]{3,}$/.test(trimmed)) {
            return <View key={idx} style={{ height: 1, backgroundColor: THEME.border, marginVertical: 12 }} />;
        }

        // Empty line
        if (!trimmed) return <View key={idx} style={styles.spacer} />;

        // Regular paragraph
        return (
            <Text key={idx} style={styles.paragraph}>
                {parseInline(trimmed, `p-${idx}`, citationMap)}
            </Text>
        );
    };

    return (
        <View style={[styles.container, style]}>
            {blocks.map(renderBlock)}
        </View>
    );
};

export default MarkdownText;
